import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/lib/LanguageContext";
import { HOSPITAL, whatsappLink } from "@/lib/i18n";
import { LAPORAN_CATEGORIES } from "@/lib/laporan";
import { useAuth } from "@/admin/AuthContext";
import { Menu, X, Languages, Phone, ChevronDown, FileBarChart2, Lock } from "lucide-react";

const NAV_ITEMS = [
  { id: "home", href: "#home" },
  // "about" is rendered as a Profil dropdown separately
  { id: "services", href: "#services" },
  { id: "doctors", href: "#doctors" },
  { id: "news", href: "#news" },
  { id: "gallery", href: "#gallery" },
  { id: "contact", href: "#contact" },
];

const Header = () => {
  const { t, lang, toggleLang } = useLanguage();
  const { user } = useAuth();
  const isAuthed = user && typeof user === "object";
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [laporanOpen, setLaporanOpen] = useState(false);
  const [profilOpen, setProfilOpen] = useState(false);
  const [mobileLaporanOpen, setMobileLaporanOpen] = useState(false);
  const [mobileProfilOpen, setMobileProfilOpen] = useState(false);
  const laporanRef = useRef(null);
  const profilRef = useRef(null);
  const location = useLocation();
  const onHome = location.pathname === "/";

  // Build hrefs that work from any route (anchor jumps back to home)
  const anchorHref = (h) => (onHome ? h : `/${h}`);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close laporan dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (laporanRef.current && !laporanRef.current.contains(e.target)) {
        setLaporanOpen(false);
      }
      if (profilRef.current && !profilRef.current.contains(e.target)) {
        setProfilOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-md border-b border-slate-100 shadow-sm"
          : "bg-white/60 backdrop-blur-sm"
      }`}
    >
      <div className="container-x flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link
          to="/"
          data-testid="header-logo"
          className="flex items-center gap-3 group"
        >
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-blue-100 blur-md opacity-60 group-hover:opacity-100 transition" />
            <img
              src={HOSPITAL.logo}
              alt="RS Bhayangkara Tarakan Logo"
              className="relative h-11 w-11 md:h-12 md:w-12 rounded-full object-cover ring-2 ring-white"
            />
          </div>
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-heading font-bold text-slate-900 text-sm md:text-base">
              RS Bhayangkara Tarakan
            </span>
            <span className="text-[10px] md:text-xs text-slate-500 tracking-wide">
              {t.header.tagline}
            </span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV_ITEMS.slice(0, 1).map((item) => (
            <a
              key={item.id}
              href={anchorHref(item.href)}
              data-testid={`nav-${item.id}`}
              className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-all"
            >
              {t.nav[item.id]}
            </a>
          ))}

          {/* Profil dropdown */}
          <div ref={profilRef} className="relative">
            <button
              type="button"
              data-testid="nav-profil"
              onClick={() => setProfilOpen((v) => !v)}
              className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-full transition-all ${
                profilOpen || location.pathname.startsWith("/profil")
                  ? "text-blue-800 bg-blue-50"
                  : "text-slate-600 hover:text-blue-800 hover:bg-blue-50"
              }`}
            >
              {t.nav.about}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${profilOpen ? "rotate-180" : ""}`} />
            </button>
            {profilOpen && (
              <div
                data-testid="nav-profil-dropdown"
                className="absolute left-0 mt-2 w-64 bg-white rounded-2xl border border-slate-100 shadow-xl shadow-slate-900/5 p-2 z-50"
              >
                <a
                  href={anchorHref("#about")}
                  onClick={() => setProfilOpen(false)}
                  data-testid="nav-profil-about"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-blue-50 group"
                >
                  <div className="h-8 w-8 rounded-lg bg-blue-50 text-blue-800 flex items-center justify-center text-sm font-bold">
                    i
                  </div>
                  <div className="text-sm font-medium text-slate-800 group-hover:text-blue-800">
                    {t.nav.about_overview}
                  </div>
                </a>
                <Link
                  to="/profil/struktur-organisasi"
                  onClick={() => setProfilOpen(false)}
                  data-testid="nav-profil-struktur"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-blue-50 group"
                >
                  <div className="h-8 w-8 rounded-lg bg-purple-50 text-purple-700 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="2" width="6" height="4" rx="1" />
                      <rect x="3" y="14" width="6" height="4" rx="1" />
                      <rect x="15" y="14" width="6" height="4" rx="1" />
                      <path d="M12 6v3M6 14v-2h12v2M12 9v3" />
                    </svg>
                  </div>
                  <div className="text-sm font-medium text-slate-800 group-hover:text-blue-800">
                    {t.nav.struktur_organisasi}
                  </div>
                </Link>
                <Link
                  to="/profil/sejarah"
                  onClick={() => setProfilOpen(false)}
                  data-testid="nav-profil-sejarah"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-blue-50 group"
                >
                  <div className="h-8 w-8 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 3v18h18" />
                      <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" />
                    </svg>
                  </div>
                  <div className="text-sm font-medium text-slate-800 group-hover:text-blue-800">
                    {t.nav.sejarah}
                  </div>
                </Link>
              </div>
            )}
          </div>

          {NAV_ITEMS.slice(1).map((item) => (
            <a
              key={item.id}
              href={anchorHref(item.href)}
              data-testid={`nav-${item.id}`}
              className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-all"
            >
              {t.nav[item.id]}
            </a>
          ))}

          {/* Laporan dropdown */}
          <div ref={laporanRef} className="relative">
            <button
              type="button"
              data-testid="nav-laporan"
              onClick={() => setLaporanOpen((v) => !v)}
              className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-full transition-all ${
                laporanOpen || location.pathname.startsWith("/laporan")
                  ? "text-blue-800 bg-blue-50"
                  : "text-slate-600 hover:text-blue-800 hover:bg-blue-50"
              }`}
            >
              {t.nav.laporan}
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform ${
                  laporanOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {laporanOpen && (
              <div
                data-testid="nav-laporan-dropdown"
                className="absolute right-0 mt-2 w-72 bg-white rounded-2xl border border-slate-100 shadow-xl shadow-slate-900/5 p-2 z-50"
              >
                <Link
                  to="/laporan"
                  onClick={() => setLaporanOpen(false)}
                  data-testid="nav-laporan-index"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-blue-50 group"
                >
                  <div className="h-8 w-8 rounded-lg bg-blue-50 text-blue-800 flex items-center justify-center">
                    <FileBarChart2 className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900 group-hover:text-blue-800">
                      {t.laporan.title}
                    </div>
                    <div className="text-xs text-slate-500">
                      {t.nav.laporan}
                    </div>
                  </div>
                </Link>
                <div className="my-1 h-px bg-slate-100" />
                {LAPORAN_CATEGORIES.map((c) => {
                  const Icon = c.icon;
                  return (
                    <Link
                      key={c.slug}
                      to={`/laporan/${c.slug}`}
                      onClick={() => setLaporanOpen(false)}
                      data-testid={`nav-laporan-${c.slug}`}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-blue-50 group"
                    >
                      <div className={`h-8 w-8 rounded-lg ${c.tint} flex items-center justify-center`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-800 group-hover:text-blue-800">
                          {t.nav[c.nav_key]}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* SIPERKASA — top-level, login required + permission-gated */}
          {(!isAuthed || user.role === "superadmin" || user.can_access_siperkasa !== false) && (
          <Link
            to={isAuthed ? "/siperkasa" : "/admin/login?next=/siperkasa"}
            data-testid="nav-siperkasa"
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-full transition-all ${
              location.pathname.startsWith("/siperkasa")
                ? "text-blue-800 bg-blue-50"
                : "text-slate-600 hover:text-blue-800 hover:bg-blue-50"
            }`}
          >
            {t.nav.siperkasa}
            {!isAuthed && <Lock className="h-3 w-3 text-slate-400" />}
          </Link>
          )}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-2">
          <button
            onClick={toggleLang}
            data-testid="language-toggle"
            aria-label="Toggle language"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-700 border border-slate-200 hover:border-blue-800 hover:text-blue-800 rounded-full transition-all"
          >
            <Languages className="h-3.5 w-3.5" />
            <span className="tabular-nums">{lang.toUpperCase()}</span>
          </button>

          <a
            href={whatsappLink(
              lang === "id"
                ? "Halo RS Bhayangkara Tarakan, saya ingin membuat janji temu."
                : "Hello RS Bhayangkara Tarakan, I would like to book an appointment."
            )}
            target="_blank"
            rel="noreferrer"
            data-testid="header-appointment-btn"
            className="hidden md:inline-flex items-center gap-2 bg-blue-800 hover:bg-blue-900 text-white text-sm font-medium px-4 py-2 rounded-full transition-all shadow-sm hover:shadow-md"
          >
            <Phone className="h-4 w-4" />
            <span>{t.nav.appointment}</span>
          </a>

          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className="lg:hidden p-2 rounded-full hover:bg-blue-50 text-slate-700"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-slate-100 bg-white/95 backdrop-blur-md"
        >
          <nav className="container-x py-4 flex flex-col gap-1">
            {NAV_ITEMS.slice(0, 1).map((item) => (
              <a
                key={item.id}
                href={anchorHref(item.href)}
                data-testid={`mobile-nav-${item.id}`}
                onClick={() => setOpen(false)}
                className="px-4 py-3 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-800 rounded-lg transition-all"
              >
                {t.nav[item.id]}
              </a>
            ))}

            {/* Mobile profil accordion */}
            <button
              type="button"
              data-testid="mobile-nav-profil"
              onClick={() => setMobileProfilOpen((v) => !v)}
              className="flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-800 rounded-lg transition-all"
            >
              <span>{t.nav.about}</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${mobileProfilOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileProfilOpen && (
              <div className="ml-3 pl-3 border-l border-blue-100 space-y-0.5">
                <a
                  href={anchorHref("#about")}
                  onClick={() => { setOpen(false); setMobileProfilOpen(false); }}
                  data-testid="mobile-nav-profil-about"
                  className="block px-4 py-2.5 text-sm text-slate-600 hover:bg-blue-50 hover:text-blue-800 rounded-lg"
                >
                  {t.nav.about_overview}
                </a>
                <Link
                  to="/profil/struktur-organisasi"
                  onClick={() => { setOpen(false); setMobileProfilOpen(false); }}
                  data-testid="mobile-nav-profil-struktur"
                  className="block px-4 py-2.5 text-sm text-slate-600 hover:bg-blue-50 hover:text-blue-800 rounded-lg"
                >
                  {t.nav.struktur_organisasi}
                </Link>
                <Link
                  to="/profil/sejarah"
                  onClick={() => { setOpen(false); setMobileProfilOpen(false); }}
                  data-testid="mobile-nav-profil-sejarah"
                  className="block px-4 py-2.5 text-sm text-slate-600 hover:bg-blue-50 hover:text-blue-800 rounded-lg"
                >
                  {t.nav.sejarah}
                </Link>
              </div>
            )}

            {NAV_ITEMS.slice(1).map((item) => (
              <a
                key={item.id}
                href={anchorHref(item.href)}
                data-testid={`mobile-nav-${item.id}`}
                onClick={() => setOpen(false)}
                className="px-4 py-3 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-800 rounded-lg transition-all"
              >
                {t.nav[item.id]}
              </a>
            ))}

            {/* Mobile laporan accordion */}
            <button
              type="button"
              data-testid="mobile-nav-laporan"
              onClick={() => setMobileLaporanOpen((v) => !v)}
              className="flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-800 rounded-lg transition-all"
            >
              <span>{t.nav.laporan}</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${mobileLaporanOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileLaporanOpen && (
              <div className="ml-3 pl-3 border-l border-blue-100 space-y-0.5">
                <Link
                  to="/laporan"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-laporan-index"
                  className="block px-4 py-2.5 text-sm text-slate-600 hover:bg-blue-50 hover:text-blue-800 rounded-lg"
                >
                  {t.laporan.title}
                </Link>
                {LAPORAN_CATEGORIES.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/laporan/${c.slug}`}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-laporan-${c.slug}`}
                    className="block px-4 py-2.5 text-sm text-slate-600 hover:bg-blue-50 hover:text-blue-800 rounded-lg"
                  >
                    {t.nav[c.nav_key]}
                  </Link>
                ))}
              </div>
            )}

            {/* SIPERKASA (mobile) — login required + permission-gated */}
            {(!isAuthed || user.role === "superadmin" || user.can_access_siperkasa !== false) && (
            <Link
              to={isAuthed ? "/siperkasa" : "/admin/login?next=/siperkasa"}
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-siperkasa"
              className="flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-800 rounded-xl"
            >
              <span>{t.nav.siperkasa}</span>
              {!isAuthed && <Lock className="h-3.5 w-3.5 text-slate-400" />}
            </Link>
            )}

            <a
              href={whatsappLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="mobile-appointment-btn"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-blue-800 text-white px-4 py-3 rounded-full text-sm font-medium"
            >
              <Phone className="h-4 w-4" />
              {t.nav.appointment}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
